import React from "react";
import Link from "next/link";
import { cn } from "@/lib/format";
import { ArrowRight } from "lucide-react";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "outline-inverse"
  | "ghost"
  | "link";

export type ButtonSize = "sm" | "md" | "lg" | "xl";

type Props = React.ButtonHTMLAttributes<HTMLButtonElement> &
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    variant?: ButtonVariant;
    size?: ButtonSize;
    href?: string;
    external?: boolean;
    arrow?: boolean;
    className?: string;
    children: React.ReactNode;
  };

const VARIANT_MAP: Record<ButtonVariant, string> = {
  // `primary` is the default action: solid brand colour for confident,
  // high-contrast structural CTAs. Reserve `secondary` for the single
  // highest-intent accent action per view (e.g. the header "Plan my trip").
  // Matches production's real .btn.btn-primary exactly (confirmed via
  // computed styles on the live site): no shadow, no hover-lift. Production
  // hovers to its "primary-100" swatch, which this codebase's token system
  // already maps to --color-primary-dark (see the comment on that token).
  primary: "bg-primary text-paper hover:bg-primary-dark",
  secondary:
    "bg-secondary text-primary-dark font-semibold hover:bg-secondary-dark hover:text-paper",
  // `outline` reads on light/paper surfaces: heading text with a hairline border
  // that fills to brand on hover. Use `outline-inverse` on dark bands.
  outline:
    "bg-white text-headings hover:bg-primary hover:text-white hover:border-primary",
  "outline-inverse":
    "bg-transparent text-paper border border-white/45 backdrop-blur-md hover:bg-white/10 hover:border-white/90",
  ghost:
    "bg-transparent text-primary border border-line hover:bg-primary hover:text-white hover:border-primary",
  link: "bg-transparent text-primary font-medium text-sm border-b border-current !p-0 !rounded-none hover:text-secondary-dark !w-auto",
};

// Sizes match production's real .btn-md/.btn-lg computed styles exactly
// (confirmed via the live site: both use an identical 8px/24px padding —
// only font-size and resulting height differ).
const SIZE_MAP: Record<ButtonSize, string> = {
  sm: "min-h-9 px-4 py-2 text-xs gap-2",
  md: "min-h-10 px-6 py-2 text-sm gap-1.5",
  lg: "min-h-[45px] px-6 py-2 text-base gap-3",
  xl: "min-h-14 px-8 py-2 text-lg gap-3",
};

const BASE_STYLES =
  "inline-flex font-bold items-center justify-center rounded transition-colors duration-200 ease-out whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary group disabled:pointer-events-none disabled:opacity-55 aria-disabled:pointer-events-none aria-disabled:opacity-55";

export const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  Props
>(
  (
    {
      variant = "primary",
      size = "md",
      href,
      external,
      arrow,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    // Determine which classes to omit if it's just a 'link' variant
    const isLink = variant === "link";
    const classes = cn(
      isLink
        ? "inline-flex items-center gap-1 transition-colors group"
        : cn(BASE_STYLES, SIZE_MAP[size]),
      VARIANT_MAP[variant],
      className,
    );
    const content = (
      <>
        {children}
        {arrow && (
          <span className="inline-block transition-transform duration-300 -rotate-45 group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
            <ArrowRight size={14} strokeWidth={2} aria-hidden />
          </span>
        )}
      </>
    );

    if (href) {
      if (external || href.startsWith("http")) {
        return (
          <a
            href={href}
            className={classes}
            ref={ref as React.Ref<HTMLAnchorElement>}
            target="_blank"
            rel="noopener noreferrer"
            {...props}
          >
            {content}
          </a>
        );
      }
      return (
        <Link
          href={href}
          className={classes}
          ref={ref as React.Ref<HTMLAnchorElement>}
          {...props}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        type={props.type ?? "button"}
        className={classes}
        ref={ref as React.Ref<HTMLButtonElement>}
        {...props}
      >
        {content}
      </button>
    );
  },
);
Button.displayName = "Button";
